import { defineStore } from 'pinia';
import { cloneDeep, get, each, find, map, startsWith } from 'lodash';
import Vue from 'vue';

import PropSetApi from '@/extensions/shop/store/api/propSet';
import PropertyApi from '@/extensions/shop/store/api/property';

import pinia from '@/pinia';
import { useSetupStore } from '@/stores';

const defaultForm = {
    key: '',
    name: '',
    description: undefined,
    properties: [],
};

const defaultRules = {
    key: 'required',
    name: 'required|min:3',
};

const defaultUi = {
    pagination: {},
    form: {
        initialized: false,
        validateOnEnter: false
    }
};

export const useShopPropSetStore = defineStore('shopPropSet', {
    state: () => ({
        loading: true,
        propSetsPaginated: {
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0,
                selection: {
                    type: 'inclusive',
                    identifiers: [],
                    total: 0,
                },
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            link: {},
            defaultSort: null,
            items: []
        },
        options: {
            property: {
                loading: false,
                items: []
            },
        },
        form: cloneDeep(defaultForm),
        rules: cloneDeep(defaultRules),
        ui: cloneDeep(defaultUi)
    }),

    getters: {
        formInitialized: (state) => get(state, 'ui.form.initialized'),
        formValidateOnEnter: (state) => get(state, 'ui.form.validateOnEnter'),
        showRelatedArticles: () => false,
        computedRules: (state) => {
            const setupStore = useSetupStore(pinia);
            let rules = cloneDeep(state.rules);

            const shop = setupStore.activeCms.shop;
            const propSet = state.form;

            if (propSet.id) {
                rules.key = `required|unique_prop_set_key:"shop":${shop.id},"propSet":${propSet.id}`;
            } else {
                rules.key = `required|unique_prop_set_key:"shop":${shop.id}`;
            }

            return rules;
        }
    },

    actions: {
        addPaginatedPropSetsToState(data) {
            this.propSetsPaginated.items = data.items;
            this.propSetsPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.propSetsPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.propSetsPaginated.link = cloneDeep(data.config.link);
            this.propSetsPaginated.defaultSort = get(data.config, 'defaultSort') || null;
            this.propSetsPaginated.showFilters = get(data.config, 'showFilters') || false;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.propSetsPaginated.allColumns, column => {
                let allowed = find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.propSetsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.propSetsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.propSetsPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.propSetsPaginated.filters, key);
            } else {
                Vue.set(this.propSetsPaginated.filters, key, value);
            }
        },

        defineSorting({ key, value }) {
            Vue.set(this.propSetsPaginated.variables, 'order', key);
            Vue.set(this.propSetsPaginated.variables, 'direction', value);
        },

        paginationBulkSelectionCheckAll({ value }) {
            let selection = this.propSetsPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'exclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', this.propSetsPaginated.pageInfo.total);
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', selection.identifiers.length);
            }
        },

        paginationBulkSelectionCheckVisible({ value }) {
            const items = get(this.propSetsPaginated, 'items') || [];
            const ids = items.map(el => parseInt(el.id));

            let selection = this.propSetsPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'inclusive');

                if (!selection.identifiers.length) {
                    Vue.set(selection, 'identifiers', ids);
                } else {
                    Vue.set(selection, 'identifiers', [...selection.identifiers, ...ids]);
                }
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', selection.identifiers.filter(el => ids.includes(el) === false));
            }

            Vue.set(selection, 'identifiers', [...new Set(selection.identifiers)]);
            Vue.set(selection, 'total', selection.identifiers.length);
        },

        paginationBulkSelectionCheckItem(item) {
            let selection = this.propSetsPaginated.pageInfo.selection;
            const id = parseInt(item.id);

            let checked = false;
            switch (selection.type) {
                case 'inclusive':
                    checked = !!~selection.identifiers.map(el => parseInt(el)).indexOf(id);
                    break;
                case 'exclusive':
                    checked = !~selection.identifiers.map(el => parseInt(el)).indexOf(id);
                    break;
            }

            let push = true;
            let incr = true;
            switch (selection.type) {
                case 'inclusive':
                    push = incr = checked === false;
                    break;
                case 'exclusive':
                    push = checked === true;
                    incr = checked === false;
                    break;
            }

            if (push === true) {
                const index = selection.identifiers.length;
                Vue.set(selection.identifiers, index, id);
            } else {
                const index = selection.identifiers.indexOf(id);
                if (index >= 0) Vue.delete(selection.identifiers, index);
            }

            if (incr === true) Vue.set(selection, 'total', selection.total + 1);
            else Vue.set(selection, 'total', selection.total - 1);
        },

        paginationBulkSelectionReset() {
            Vue.set(this.propSetsPaginated.pageInfo, 'selection', {
                type: 'inclusive',
                identifiers: [],
                total: 0,
            });
        },

        updateLoading(value) {
            this.loading = value;
        },

        defineFilterVisibility(value) {
            Vue.set(this.propSetsPaginated, 'showFilters', value);
        },

        updateRules(rules) {
            Vue.set(this, 'rules', rules);
        },

        resetRules() {
            Vue.set(this, 'rules', cloneDeep(defaultRules));
        },

        resetUI() {
            Vue.set(this.ui.form, 'initialized', false);
        },

        insertPropSet(propSet) {
            Vue.set(this, 'form', propSet);
        },

        updatePropertyOptionsLoading(value) {
            Vue.set(this.options.property, 'loading', value);
        },

        insertPropertyOptions(properties) {
            Vue.set(this.options.property, 'items', properties);
        },

        setFormInitialized(flag) {
            Vue.set(this.ui.form, 'initialized', flag);
        },

        setFormValidateOnEnter(flag) {
            Vue.set(this.ui.form, 'validateOnEnter', flag);
        },

        setFormKey(value) {
            Vue.set(this.form, 'key', value);
        },

        setFormName(value) {
            Vue.set(this.form, 'name', value);
        },

        setFormDescription(value) {
            Vue.set(this.form, 'description', value);
        },

        setFormProperties(value) {
            Vue.set(this.form, 'properties', value);
        },

        addFormTranslation(translation) {
            if (this.form.translationOrigin) {
                const translations = get(this.form.translationOrigin, 'translations', []);
                let i = translations.findIndex(el => el.language.code === translation.language.code);
                if (i < 0) i = translations.length;
                Vue.set(this.form.translationOrigin.translations, i, translation);
            } else {
                const translations = get(this.form, 'translations', []);
                let i = translations.findIndex(el => el.language.code === translation.language.code);
                if (i < 0) i = translations.length;
                Vue.set(this.form.translations, i, translation);
            }
        },

        removeFormTranslation(translation) {
            if (this.form.translationOrigin) {
                const translations = get(this.form.translationOrigin, 'translations', []);
                let i = translations.findIndex(el => el.language.code === translation.language.code);
                if (i >= 0) Vue.delete(this.form.translationOrigin.translations, i);
            } else {
                const translations = get(this.form, 'translations', []);
                let i = translations.findIndex(el => el.language.code === translation.language.code);
                if (i >= 0) Vue.delete(this.form.translations, i);
            }
        },

        // Async actions
        fetchPropSetsPaginated() {
            return PropSetApi.fetchPropSetsPaginated(this.propSetsPaginated).then(data => {
                this.addPaginatedPropSetsToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        processPaginationBulkAction(action) {
            return PropSetApi.processPropSetPaginationBulkAction(action, this.propSetsPaginated);
        },

        fetchPropSet(id) {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;
            if (cms && cms.shop && cms.shop.id) {
                return PropSetApi.fetchPropSet(id, cms.shop.id).then(propSet => {
                    this.insertPropSet(cloneDeep(propSet));
                });
            }
        },

        fetchPropertyOptions(shop) {
            this.updatePropertyOptionsLoading(true);
            return PropertyApi.fetchList(shop)
                .then(properties => {
                    this.updatePropertyOptionsLoading(false);
                    this.insertPropertyOptions(properties);
                })
                .catch(err => {
                    this.updatePropertyOptionsLoading(false);
                    throw err;
                });
        },

        translate({ propSet, language, propSets, type }) {
            return PropSetApi.translate(propSet, language, propSets, type);
        },

        save(propSet) {
            const setupStore = useSetupStore(pinia);
            if (!propSet.id || startsWith(propSet.id, 'new:')) {
                propSet.shop = { id: setupStore.activeCms.shop.id };
                return this.create(propSet);
            } else {
                return this.update(propSet);
            }
        },

        create(propSet) {
            return PropSetApi.create(propSet).then(async propSet => {
                this.insertPropSet(cloneDeep(propSet));
            });
        },

        update(propSet) {
            let translation = false;
            if (propSet.translationOrigin) translation = true;
            return PropSetApi.update(propSet, translation).then(async propSet => {
                this.insertPropSet(cloneDeep(propSet));
            });
        },

        delete(id) {
            return PropSetApi.delete(id).then(() => {
                this.reset();
                this.resetUI();
            });
        },

        clonePropSet(id) {
            return PropSetApi.clone(id);
        },

        reset() {
            this.insertPropSet(cloneDeep(defaultForm));
        }
    }
});
